/**
 * DRFInfoInputs - Core Component (Production-Ready)
 * Clean input component for DRF configuration without demo UI chrome.
 * Used in: Project workflow pages
 *
 * For demo page with collapsible UI, see DRFInfoInputsDemoWrapper.tsx
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DOMAIN KNOWLEDGE: Debt Reserve Fund (DRF) Configuration (Michigan Municipal Bonds)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This component configures the district's actual bank account for debt service.
 * The DRF is critical for ensuring bond payments can be made even when revenue
 * timing doesn't perfectly align with payment dates.
 *
 * Key Concepts:
 *
 * 1. STARTING BALANCE
 *    - Current actual balance in the DRF account
 *    - Shows district's current reserves
 *    - Must be between minimum and maximum balance
 *    - Determines available capacity for new debt
 *    - Used as initial balance for cash flow calculations
 *
 * 2. MINIMUM BALANCE
 *    - Hard floor - the DRF cannot go below this amount
 *    - Policy-driven level based on district's risk tolerance
 *    - Going below zero means bounced payments - absolutely cannot happen
 *    - Used as constraint in candidate validation
 *    - Any cash flow that drives DRF below minimum fails
 *
 * 3. MAXIMUM BALANCE
 *    - Policy-driven ceiling indicating capacity for additional bonds
 *    - Available capacity = maximum - starting balance
 *    - Indicates room for new debt
 *    - Districts set based on comfort with risk and cash flow patterns
 *    - Used to determine bond feasibility
 *
 * 4. BALANCE CONSTRAINTS
 *    - Minimum < Starting < Maximum (strict ordering required)
 *    - All balances must be non-negative
 *    - Starting balance position determines status (low/medium/high)
 *    - Balance spread (max - min) shows operating range
 *
 * 5. DRF STATUS CLASSIFICATION
 *    - Low: Starting balance < 33% of range (closer to minimum)
 *    - Medium: Starting balance 33-67% of range
 *    - High: Starting balance > 67% of range (closer to maximum)
 *    - Status indicates risk level and capacity for new debt
 *
 * Why This Matters:
 * - DRF is the district's actual bank account for debt service
 * - Going below zero means bounced payments - absolutely cannot happen
 * - Proper DRF management ensures districts can weather revenue timing mismatches
 * - Available capacity determines bond feasibility
 * - Balance constraints are hard limits in candidate validation
 *
 * Processing Pipeline Impact:
 * - DRF constraints determine candidate viability
 * - Any cash flow that drives DRF below minimum fails validation
 * - Available capacity (max - starting) indicates room for new debt
 * - Starting balance position affects risk assessment
 * - Used in cash flow calculations throughout bond lifecycle
 *
 * Common Mistake:
 * ❌ Don't set minimum >= maximum balance
 * ❌ Don't set starting balance outside min/max range
 * ❌ Don't allow negative balances
 * ✅ Minimum < Starting < Maximum (strict ordering)
 * ✅ All balances must be non-negative
 * ✅ Set realistic levels based on district's financial situation
 *
 * For More Context:
 * @see apps/web/src/builders/drf-info/_drf-info.md - Builder overview
 * @see docs/business-context/process-overview.md - How DRF feeds into processing pipeline
 */

import { Box, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useMemo } from 'react';

import { CurrencyInput } from '~/components/inputs';
import { useSidebar } from '~/components/layout/shared/SidebarContext';
import { useSimpleInputValidation } from '~/hooks/useSimpleInputValidation';
import type { DRFInputs, DRFInputsForm } from '~/types';
import { parseFormattedNumber } from '~/utils/numberParsing';

interface DRFInfoInputsProps {
  values: DRFInputsForm;
  onUpdate: (values: DRFInputsForm) => void;
  onBlur?: () => void;
  showValidation?: boolean;
  isDisabled?: boolean;
  onValidationChange?: (errors: string[], warnings: string[]) => void;
}

/**
 * Convert form values to engine format for validation
 * Returns null if any required field is empty or invalid
 */
const convertFormToEngineForValidation = (formValues: DRFInputsForm): DRFInputs | null => {
  // Check all fields are present
  if (
    !formValues.startingBalance?.trim() ||
    !formValues.minimumBalance?.trim() ||
    !formValues.maximumBalance?.trim()
  ) {
    return null;
  }

  // Parse and validate numeric values
  // Note: CurrencyInput returns formatted strings, so we need to strip formatting
  const startingBalance = parseFormattedNumber(formValues.startingBalance);
  const minimumBalance = parseFormattedNumber(formValues.minimumBalance);
  const maximumBalance = parseFormattedNumber(formValues.maximumBalance);

  // Check if all values are valid numbers
  if (
    !Number.isFinite(startingBalance) ||
    !Number.isFinite(minimumBalance) ||
    !Number.isFinite(maximumBalance)
  ) {
    return null;
  }

  return {
    startingBalance,
    minimumBalance,
    maximumBalance,
  };
};

export const DRFInfoInputs: React.FC<DRFInfoInputsProps> = ({
  values,
  onUpdate,
  onBlur,
  showValidation = false,
  isDisabled = false,
  onValidationChange,
}) => {
  const { isExpanded } = useSidebar();

  // Custom validation function for DRF (only negative values are errors)
  const validateDRFInputs = (engineInputs: DRFInputs) => {
    const errors: string[] = [];
    if (engineInputs.startingBalance < 0) {
      errors.push('Starting balance cannot be negative');
    }
    if (engineInputs.minimumBalance < 0) {
      errors.push('Minimum balance cannot be negative');
    }
    if (engineInputs.maximumBalance < 0) {
      errors.push('Maximum balance cannot be negative');
    }
    return { isValid: errors.length === 0, errors };
  };

  // Use shared validation hook for errors
  const { handlers, computed } = useSimpleInputValidation<Record<string, string>, DRFInputs>({
    values: values as unknown as Record<string, string>,
    onUpdate: onUpdate as unknown as (values: Record<string, string>) => void,
    onBlur,
    showValidation,
    requiredFields: ['startingBalance', 'minimumBalance', 'maximumBalance'],
    convertToEngine: convertFormToEngineForValidation as unknown as (
      formValues: Record<string, string>
    ) => DRFInputs | null,
    validate: validateDRFInputs,
    mapErrorsToFields: (error) => {
      if (error.includes('Starting balance')) {
        return 'startingBalance';
      } else if (error.includes('Minimum balance')) {
        return 'minimumBalance';
      } else if (error.includes('Maximum balance')) {
        return 'maximumBalance';
      }
      return null;
    },
  });

  const { handleInputChange, markTouched } = handlers;
  const { validationErrors, allFieldsTouched } = computed;

  // Warnings: Informational messages about balance relationships (don't block submission)
  // Note: Warnings are component-specific logic, not using Builder validation
  const validationWarnings = useMemo(() => {
    const engineInputs = convertFormToEngineForValidation(values);
    if (!engineInputs) {
      return [];
    }

    const warnings: string[] = [];

    // Only show warnings if all fields are filled
    if (!allFieldsTouched) {
      return [];
    }

    if (engineInputs.minimumBalance >= engineInputs.maximumBalance) {
      warnings.push('Minimum balance should typically be less than maximum balance');
    }

    if (engineInputs.startingBalance < engineInputs.minimumBalance) {
      warnings.push('Starting balance is below the minimum balance');
    }

    if (engineInputs.startingBalance > engineInputs.maximumBalance) {
      warnings.push('Starting balance exceeds the maximum balance');
    }

    return warnings;
  }, [values, allFieldsTouched]);

  // Notify parent of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validationErrors, validationWarnings);
    }
  }, [validationErrors, validationWarnings, onValidationChange]);

  return (
    <VStack
      align="stretch"
      boxShadow="md"
      gap={6}
      border="1px solid"
      borderColor="gray.200"
      p={4}
      borderRadius="md"
      bg={isDisabled ? 'gray.50' : undefined}
      pointerEvents={isDisabled ? 'none' : undefined}
      opacity={isDisabled ? 0.8 : 1}
    >
      <Heading size="md">Debt Reserve Fund Configuration</Heading>

      <SimpleGrid columns={{ base: 3 }} gap={3} alignItems="flex-end">
        <CurrencyInput
          label={isExpanded ? 'Start Balance' : 'Starting Balance'}
          value={values.startingBalance || ''}
          onChange={(value) => handleInputChange('startingBalance', value)}
          onBlur={() => markTouched('startingBalance')}
          min={0}
          size="sm"
          isRequired
        />

        <CurrencyInput
          label={isExpanded ? 'Min Balance' : 'Minimum Balance'}
          value={values.minimumBalance || ''}
          onChange={(value) => handleInputChange('minimumBalance', value)}
          onBlur={() => markTouched('minimumBalance')}
          min={0}
          size="sm"
          isRequired
        />

        <CurrencyInput
          label={isExpanded ? 'Max Balance' : 'Maximum Balance'}
          value={values.maximumBalance || ''}
          onChange={(value) => handleInputChange('maximumBalance', value)}
          onBlur={() => markTouched('maximumBalance')}
          min={0}
          size="sm"
          isRequired
        />
      </SimpleGrid>

      {/* Validation Messages - Errors (red) */}
      {(showValidation || allFieldsTouched) && validationErrors.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" mb={2}>
            Please fix the following issues:
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationErrors.map((error) => (
              <Text key={error} fontSize="xs" color="red.600">
                • {error}
              </Text>
            ))}
          </VStack>
        </Box>
      )}

      {/* Validation Messages - Warnings (yellow) */}
      {validationWarnings.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="yellow.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="yellow.400"
        >
          <Text fontSize="xs" fontWeight="bold" color="yellow.800" mb={2}>
            Note: Informational warnings (these won't prevent submission):
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationWarnings.map((warning) => (
              <Text key={warning} fontSize="xs" color="yellow.700">
                ⚠ {warning}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

// Utility function to convert form values to engine format
export const convertToEngineFormat = (formValues: DRFInputsForm): DRFInputs => {
  return {
    startingBalance: parseFormattedNumber(formValues.startingBalance) || 0,
    minimumBalance: parseFormattedNumber(formValues.minimumBalance) || 0,
    maximumBalance: parseFormattedNumber(formValues.maximumBalance) || 0,
  };
};

// Utility function to convert engine format to form values
export const convertFromEngineFormat = (engineValues: DRFInputs): DRFInputsForm => {
  return {
    startingBalance: engineValues.startingBalance.toString(),
    minimumBalance: engineValues.minimumBalance.toString(),
    maximumBalance: engineValues.maximumBalance.toString(),
  };
};

export type { DRFInputsForm };
